import { useCallback, useRef, useState } from 'react';

interface UseToastOptions {
  duration?: number; // in milliseconds
  vertical?: 'top' | 'bottom';
  horizontal?: 'left' | 'center' | 'right';
}

interface ToastState {
  open: boolean;
  message: string;
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}

export const useToast = (defaultOptions: UseToastOptions = {}) => {
  const {
    duration = 4000,
    vertical = 'top',
    horizontal = 'center',
  } = defaultOptions;

  const [toastState, setToastState] = useState<ToastState>({
    open: false,
    message: '',
    vertical,
    horizontal,
  });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toast = useCallback(
    (message: string, options: UseToastOptions = {}) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const finalOptions = { ...defaultOptions, ...options };

      setToastState({
        open: true,
        message,
        vertical: finalOptions.vertical || vertical,
        horizontal: finalOptions.horizontal || horizontal,
      });

      // Set auto-close timer
      timeoutRef.current = setTimeout(() => {
        setToastState((prev) => ({ ...prev, open: false }));
      }, finalOptions.duration || duration);
    },
    [duration, vertical, horizontal, defaultOptions]
  );

  const handleClose = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setToastState((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    toast,
    toastProps: {
      open: toastState.open,
      handleClose,
      message: toastState.message,
      vertical: toastState.vertical,
      horizontal: toastState.horizontal,
    },
  };
};
